"use client";

import { c as _c } from "react/compiler-runtime";
import { Copy, Code2 } from "lucide-react";
import { useTheme } from "next-themes";
import { Highlight, themes } from "prism-react-renderer";
import { useState, useEffect } from "react";
import { Button } from "@/lib/components/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/lib/components/dialog";
import { toast } from "@/lib/components/use-toast";
import { cn } from "@/lib/utils";
import type { Language } from "prism-react-renderer";
import type { CSSProperties } from "react";
interface ModelCodeExampleDialogProps {
  modelId: string;
}
export function ModelCodeExampleDialog(t0) {
  const $ = _c(47);
  const {
    modelId
  } = t0;
  const [activeTab, setActiveTab] = useState("curl");
  const {
    resolvedTheme
  } = useTheme();
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setMounted(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const t3 = `curl -X POST https://llm-gw.agenzo.com/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $LLM_GATEWAY_API_KEY" \\
  -d '{
  "model": "${modelId}",
  "messages": [
    {"role": "user", "content": "Hello, how are you?"}
  ]
}'`;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      label: "cURL",
      language: "bash",
      code: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.LLM_GATEWAY_API_KEY, // or your API key string
  baseURL: "https://llm-gw.agenzo.com/v1/"
});

const response = await client.chat.completions.create({
  model: "${modelId}",
  messages: [
    { role: "user", content: "Hello, how are you?" }
  ]
});

console.log(response.choices[0].message.content);`;
  let t6;
  if ($[4] !== t5) {
    t6 = {
      label: "OpenAI SDK",
      language: "typescript",
      code: t5
    };
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = `import { createLLMGateway } from "@llmgateway/ai-sdk-provider";
import { generateText } from "ai";

const llmgateway = createLLMGateway({ apiKey: process.env.LLM_GATEWAY_API_KEY });

const { text } = await generateText({
  model: llmgateway("${modelId}"),
  prompt: "Write a vegetarian lasagna recipe for 4 people.",
});`;
  let t8;
  if ($[6] !== t7) {
    t8 = {
      label: "AI SDK",
      language: "typescript",
      code: t7
    };
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t4 || $[9] !== t6 || $[10] !== t8) {
    t9 = {
      curl: t4,
      openai: t6,
      "ai-sdk": t8
    };
    $[8] = t4;
    $[9] = t6;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const codeExamples = t9;
  const currentExample = codeExamples[activeTab];
  let copyToClipboard;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    copyToClipboard = async (text, languageLabel) => {
      ;
      try {
        await navigator.clipboard.writeText(text);
        touchToast({
          title: "Copied to clipboard",
          description: `${languageLabel} example has been copied to your clipboard.`
        });
      } catch (t10) {
        const err = t10;
        console.error("Failed to copy text: ", err);
        touchToast({
          title: "Copy failed",
          description: "Could not copy to clipboard. Please try again.",
          variant: "destructive"
        });
      }
    };
    const touchToast = _temp;
    $[12] = copyToClipboard;
  } else {
    copyToClipboard = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DialogTrigger asChild={true}><Button variant="ghost" size="sm" className="h-5 w-5 p-0" type="button" aria-label="View code examples"><Code2 className="h-3 w-3" /></Button></DialogTrigger>;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DialogTitle>Use this model with LLM Gateway</DialogTitle>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== modelId) {
    t12 = <DialogHeader>{t11}<DialogDescription>Code examples for{" "}<code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{modelId}</code>{" "}using cURL, the OpenAI SDK, and the AI SDK.</DialogDescription></DialogHeader>;
    $[15] = modelId;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== codeExamples) {
    t13 = Object.keys(codeExamples);
    $[17] = codeExamples;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = t13 as Array<"curl" | "openai" | "ai-sdk">;
  let t15;
  if ($[19] !== activeTab || $[20] !== codeExamples || $[21] !== t14) {
    t15 = <div className="flex flex-wrap gap-2">{t14.map(key => {
        const example = codeExamples[key];
        return <button key={key} type="button" onClick={() => setActiveTab(key)} className={cn("rounded-md px-3 py-1.5 text-xs font-medium transition-colors", activeTab === key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80")}>{example.label}</button>;
      })}</div>;
    $[19] = activeTab;
    $[20] = codeExamples;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== currentExample.label) {
    t16 = <span className="text-xs font-medium text-muted-foreground">{currentExample.label} example</span>;
    $[23] = currentExample.label;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== currentExample.code || $[26] !== currentExample.label) {
    t17 = () => copyToClipboard(currentExample.code, currentExample.label);
    $[25] = currentExample.code;
    $[26] = currentExample.label;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Copy className="mr-1 h-3 w-3" />;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t17) {
    t19 = <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={t17}>{t18}Copy</Button>;
    $[29] = t17;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t16 || $[32] !== t19) {
    t20 = <div className="flex items-center justify-between border-b bg-muted px-3 py-2">{t16}{t19}</div>;
    $[31] = t16;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  const t21 = mounted && resolvedTheme === "dark" ? themes.dracula : themes.github;
  let t22;
  if ($[34] !== currentExample.code || $[35] !== currentExample.language || $[36] !== t21) {
    t22 = <div className="bg-background"><Highlight code={currentExample.code} language={currentExample.language} theme={t21}>{_temp2}</Highlight></div>;
    $[34] = currentExample.code;
    $[35] = currentExample.language;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t20 || $[39] !== t22) {
    t23 = <div className="overflow-hidden rounded-md border bg-card">{t20}{t22}</div>;
    $[38] = t20;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t15 || $[42] !== t23) {
    t24 = <div className="space-y-4">{t15}{t23}</div>;
    $[41] = t15;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t12 || $[45] !== t24) {
    t25 = <Dialog>{t10}<DialogContent className="sm:max-w-2xl">{t12}{t24}</DialogContent></Dialog>;
    $[44] = t12;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  return t25;
}
function _temp2(t0) {
  const {
    className,
    style,
    tokens,
    getLineProps,
    getTokenProps
  } = t0;
  return <pre className={cn("max-h-80 overflow-auto p-4 text-xs leading-relaxed font-mono", className)} style={style}>{tokens.map((line, i) => {
      const lineProps = getLineProps({
        line
      });
      return <div key={i} {...lineProps}>{line.map((token, key_0) => {
          const tokenProps = getTokenProps({
            token
          });
          return <span key={key_0} {...tokenProps} />;
        })}</div>;
    })}</pre>;
}
function _temp(args) {
  toast({
    duration: 3000,
    ...args
  });
}